import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router'
import type { CommissionPayoutItemView, CommissionPayoutView, EligibleCommissionGroup, WaitingCommissionGroup } from '@fitting-room/contracts'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { PageHeader } from '../../components/admin/PageHeader'
import { StaffMark } from '../../components/admin/StaffMark'
import { useCalculateCommission, useCommission, useConfirmPayout, useDiscardPayout, useUpdatePayoutNote } from '../../lib/adminQueries'
import { boutiqueDateTime } from '../../lib/boutique'
import {
  ISSUE_LABEL,
  flaggedItems,
  groupSelection,
  payoutHeadline,
  rateOfBase,
  selectionSummary,
  settledWords,
  toggleGroup,
  toggleOrder,
  waitingWords,
} from '../../lib/commission'
import { formatCents, formatRate } from '../../lib/orders'
import { useStaffToken } from '../../lib/staffToken'

/** How many confirmed payouts the history shows at a time. */
const HISTORY_PAGE = 20

/**
 * The owner's commission screen, after `jovita-erp`'s portal commission
 * (`PortalCommissionNew.js`): what each person is owed and what has been
 * paid. `docs/22-back-office.md` § "Commission".
 *
 * Three parts, top to bottom, in the order the work happens. Pending payouts
 * are pinned first — calculated, not paid yet — each with the note that says
 * how it will be paid, Confirm and Discard. Under them, what is not yet
 * commissioned: first the orders that have settled — paid in full and picked
 * up — grouped by who sold them: a group opens to its orders, any of them can
 * be picked, a person's box picks them all, and the bar along the bottom says
 * what is picked and calculates it — one pending payout per person. Then the
 * orders still waiting to settle, and why, so the owner can see what is
 * coming and nobody has to ask where theirs is. Last, what has been paid out.
 */
export function CommissionPage() {
  const { token } = useStaffToken()
  const [offset, setOffset] = useState(0)
  const query = useCommission(token, { limit: HISTORY_PAGE, offset })
  const data = query.data
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const calculate = useCalculateCommission(token)
  const pendingRef = useRef<HTMLElement>(null)

  const summary = useMemo(() => selectionSummary(data?.eligible ?? [], selected), [data, selected])

  const runCalculate = () => {
    calculate.mutate(summary.orderIds, {
      onSuccess: () => {
        setSelected(new Set())
        pendingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      },
    })
  }

  return (
    <div className={summary.count > 0 ? 'pb-28' : undefined}>
      <PageHeader eyebrow="What each person is owed, and what's been paid" title="Commission" />

      {query.isPending ? <p className="st-meta">Loading…</p> : null}
      {query.isError ? (
        <p className="st-alert mb-4" role="alert">
          Couldn&rsquo;t load commission. Try again in a moment.
        </p>
      ) : null}

      {data ? (
        <div className="space-y-6">
          <section ref={pendingRef} aria-labelledby="commission-pending" className="scroll-mt-4">
            <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
              <h2 id="commission-pending" className="st-eyebrow">
                Pending payouts
              </h2>
              <span className="st-meta text-xs">calculated, not paid yet</span>
            </div>
            {data.pending.length === 0 ? (
              <p className="st-card st-meta px-4 py-3 text-sm">
                Nothing pending. Pick orders below and calculate to make a payout.
              </p>
            ) : (
              <ul className="space-y-3">
                {data.pending.map((payout) => (
                  <li key={payout.id}>
                    <PendingPayout payout={payout} />
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby="commission-unpaid">
            <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
              <h2 id="commission-unpaid" className="st-eyebrow">
                Not yet commissioned
              </h2>
              <span className="st-meta text-xs">in no payout yet</span>
            </div>
            <p className="st-meta mb-3 text-xs">
              An order earns commission once it&rsquo;s paid in full and picked up. Only bridal stylists with a commission
              rate in{' '}
              <Link to="/admin/settings" className="underline decoration-[color:var(--st-rule-strong)] underline-offset-2">
                Settings → Team
              </Link>{' '}
              are counted.
            </p>

            <h3 id="commission-eligible" className="mb-2 text-sm font-medium">
              Eligible now
            </h3>
            {data.eligible.length === 0 ? (
              <p className="st-card st-meta px-4 py-3 text-sm">
                No orders to calculate. An order shows up here once it&rsquo;s paid in full and picked up.
              </p>
            ) : (
              <ul className="space-y-3" aria-labelledby="commission-eligible">
                {data.eligible.map((group) => (
                  <li key={group.staffMemberId}>
                    <EligibleGroup group={group} selected={selected} onSelect={setSelected} />
                  </li>
                ))}
              </ul>
            )}

            <h3 id="commission-waiting" className="mb-2 mt-5 text-sm font-medium">
              Waiting
              <span className="st-meta ml-2 text-xs font-normal">a balance due, or something still to pick up</span>
            </h3>
            {data.waiting.length === 0 ? (
              <p className="st-card st-meta px-4 py-3 text-sm">Nothing waiting. Every order with commission on it has settled.</p>
            ) : (
              <ul className="space-y-3" aria-labelledby="commission-waiting">
                {data.waiting.map((group) => (
                  <li key={group.staffMemberId}>
                    <WaitingGroup group={group} />
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby="commission-history">
            <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
              <h2 id="commission-history" className="st-eyebrow">
                Paid out
              </h2>
              <span className="st-meta text-xs">confirmed, newest first</span>
            </div>
            {data.history.length === 0 ? (
              <p className="st-card st-meta px-4 py-3 text-sm">Nothing paid out yet.</p>
            ) : (
              <div className="st-card px-4 py-1">
                <ul className="divide-y divide-[color:var(--st-rule)]">
                  {data.history.map((payout) => (
                    <li key={payout.id}>
                      <ConfirmedPayout payout={payout} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {data.historyTotal > HISTORY_PAGE ? (
              <div className="mt-3 flex items-center justify-between gap-2 text-sm">
                <button type="button" className="st-btn-ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - HISTORY_PAGE))}>
                  Newer
                </button>
                <span className="st-meta text-xs">
                  {offset + 1}–{Math.min(offset + HISTORY_PAGE, data.historyTotal)} of {data.historyTotal}
                </span>
                <button
                  type="button"
                  className="st-btn-ghost"
                  disabled={offset + HISTORY_PAGE >= data.historyTotal}
                  onClick={() => setOffset(offset + HISTORY_PAGE)}
                >
                  Older
                </button>
              </div>
            ) : null}
          </section>
        </div>
      ) : null}

      {summary.count > 0 ? (
        <div
          className="print-hide fixed inset-x-0 bottom-0 z-30 border-t"
          style={{ borderColor: 'var(--st-rule-strong)', background: 'var(--st-surface)', boxShadow: 'var(--st-shadow-lifted)' }}
          data-testid="commission-selection"
        >
          <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3">
            <div className="min-w-0 flex-1 text-sm">
              <span className="font-medium">
                {summary.count === 1 ? '1 order' : `${summary.count} orders`} · {formatCents(summary.totalMinor)}
              </span>
              <span className="st-meta block text-xs">
                {summary.people === 1 ? 'One pending payout' : `A pending payout for each of ${summary.people} people`}, nothing paid
                until you confirm.
              </span>
            </div>
            <button type="button" className="st-btn-quiet" onClick={() => setSelected(new Set())}>
              Clear
            </button>
            <button type="button" className="st-btn" disabled={calculate.isPending} onClick={runCalculate}>
              {calculate.isPending ? 'Calculating…' : 'Calculate'}
            </button>
            {calculate.error ? (
              <p className="st-alert w-full" role="alert">
                {calculate.error.message}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

/* --------------------------------- eligible -------------------------------- */

function EligibleGroup({
  group,
  selected,
  onSelect,
}: {
  readonly group: EligibleCommissionGroup
  readonly selected: ReadonlySet<string>
  readonly onSelect: (next: ReadonlySet<string>) => void
}) {
  const [open, setOpen] = useState(false)
  const state = groupSelection(group, selected)
  const all = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (all.current) all.current.indeterminate = state === 'some'
  }, [state])

  return (
    <div className="st-card" data-testid="commission-group">
      <div className="flex items-center gap-3 px-4 py-3">
        <input
          ref={all}
          type="checkbox"
          className="size-4 shrink-0"
          checked={state === 'all'}
          onChange={() => onSelect(toggleGroup(group, selected))}
          aria-label={`Pick all of ${group.displayName}'s orders`}
        />
        <button
          type="button"
          className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1 text-left"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <span className="flex min-w-0 items-center gap-1.5 font-medium">
            {open ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
            <StaffMark mark={group.calendarMark} name={group.displayName} />
            <span className="truncate">{group.displayName}</span>
          </span>
          <span className="st-meta text-sm">
            {formatRate(group.rateBps)} · {group.count === 1 ? '1 order' : `${group.count} orders`}
          </span>
          <span className="ml-auto font-medium tabular-nums">{formatCents(group.totalMinor)}</span>
        </button>
      </div>
      {open ? (
        <ul className="divide-y divide-[color:var(--st-rule)] border-t" style={{ borderColor: 'var(--st-rule)' }}>
          {group.rows.map((row) => (
            <li key={row.orderId}>
              <label className="flex cursor-pointer items-start gap-3 px-4 py-2.5 text-sm" data-testid="commission-row">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 shrink-0"
                  checked={selected.has(row.orderId)}
                  onChange={() => onSelect(toggleOrder(row.orderId, selected))}
                  aria-label={`Pick ${row.orderNo}, ${row.customerName}`}
                />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{row.customerName}</span>
                  <span className="st-meta block text-xs">
                    <Link
                      to={`/admin/orders/${row.orderId}`}
                      className="underline decoration-[color:var(--st-rule-strong)] underline-offset-2"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {row.orderNo}
                    </Link>{' '}
                    · {settledWords(row)}
                  </span>
                </span>
                <span className="shrink-0 text-right tabular-nums">
                  <span className="block font-medium">{formatCents(row.amountMinor)}</span>
                  <span className="st-meta block text-xs">{rateOfBase(row.rateBps, row.baseMinor)}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/**
 * One person's orders still waiting to settle: what each will earn at
 * today's rate, and what it is waiting on. Nothing to pick here — an order
 * moves up to Eligible now by itself, the day it is paid and picked up.
 */
function WaitingGroup({ group }: { readonly group: WaitingCommissionGroup }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="st-card" data-testid="commission-waiting-group">
      <button
        type="button"
        className="flex w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="flex min-w-0 items-center gap-1.5 font-medium">
          {open ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
          <StaffMark mark={group.calendarMark} name={group.displayName} />
          <span className="truncate">{group.displayName}</span>
        </span>
        <span className="st-meta text-sm">
          {formatRate(group.rateBps)} · {group.count === 1 ? '1 order' : `${group.count} orders`}
        </span>
        <span className="st-meta ml-auto tabular-nums">{formatCents(group.totalMinor)} to come</span>
      </button>
      {open ? (
        <ul className="divide-y divide-[color:var(--st-rule)] border-t" style={{ borderColor: 'var(--st-rule)' }}>
          {group.rows.map((row) => (
            <li key={row.orderId} className="flex items-start gap-3 px-4 py-2.5 text-sm" data-testid="commission-waiting-row">
              <span className="min-w-0 flex-1">
                <span className="block font-medium">{row.customerName}</span>
                <span className="st-meta block text-xs">
                  <Link to={`/admin/orders/${row.orderId}`} className="underline decoration-[color:var(--st-rule-strong)] underline-offset-2">
                    {row.orderNo}
                  </Link>{' '}
                  · {waitingWords(row)}
                </span>
              </span>
              <span className="shrink-0 text-right tabular-nums">
                <span className="block">{formatCents(row.amountMinor)}</span>
                <span className="st-meta block text-xs">{rateOfBase(row.rateBps, row.baseMinor)}</span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/* --------------------------------- payouts -------------------------------- */

/**
 * A pending payout, pinned at the top: what it is, its orders, and the three
 * things to do with it. The note saves when it is left; Confirm sends it as
 * it is and asks once more, because confirmed is final.
 */
function PendingPayout({ payout }: { readonly payout: CommissionPayoutView }) {
  const { token } = useStaffToken()
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState(payout.note ?? '')
  const [asking, setAsking] = useState<'confirm' | 'discard' | null>(null)
  const save = useUpdatePayoutNote(token, payout.id)
  const confirm = useConfirmPayout(token, payout.id)
  const discard = useDiscardPayout(token, payout.id)
  const flagged = flaggedItems(payout)
  const error = save.error ?? confirm.error ?? discard.error

  useEffect(() => setNote(payout.note ?? ''), [payout.note])

  const saveNote = () => {
    if (note.trim() !== (payout.note ?? '')) save.mutate(note.trim())
  }

  return (
    <article className="st-card" data-testid="pending-payout" style={{ borderColor: 'var(--st-gold)' }}>
      <button type="button" className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="flex min-w-0 items-center gap-1.5">
          {open ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
          <StaffMark mark={payout.staffMark} name={payout.staffName} />
          <span className="font-medium" data-testid="payout-headline">
            {payoutHeadline(payout)}
          </span>
        </span>
        <span className="st-meta text-xs">
          {payout.items.length === 1 ? '1 order' : `${payout.items.length} orders`} · calculated by {payout.createdByLabel}
        </span>
        {flagged > 0 ? (
          <span className="st-badge st-badge--alert">{flagged === 1 ? '1 order changed' : `${flagged} orders changed`}</span>
        ) : null}
      </button>

      {open ? <PayoutItems items={payout.items} /> : null}

      <div className="border-t px-4 py-3" style={{ borderColor: 'var(--st-rule)' }}>
        <label className="flex flex-col gap-1">
          <span className="st-eyebrow">How it&rsquo;s paid</span>
          <input
            className="st-input"
            maxLength={500}
            placeholder="Like: Zelle on Oct 5, or cash from the drawer"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            onBlur={saveNote}
          />
        </label>
        {asking === 'confirm' ? (
          <div className="st-note mt-3 text-sm" role="group" aria-label="Confirm this payout">
            <p>
              Confirm {formatCents(payout.totalMinor)} paid to {payout.staffName} — &ldquo;{note.trim()}&rdquo;? It&rsquo;s
              final: a confirmed payout can&rsquo;t be changed or discarded.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                className="st-btn"
                disabled={confirm.isPending}
                onClick={() => confirm.mutate(note.trim(), { onSuccess: () => setAsking(null) })}
              >
                {confirm.isPending ? 'Confirming…' : 'Yes, confirm'}
              </button>
              <button type="button" className="st-btn-ghost" onClick={() => setAsking(null)}>
                Not yet
              </button>
            </div>
          </div>
        ) : asking === 'discard' ? (
          <div className="st-note mt-3 text-sm" role="group" aria-label="Discard this payout">
            <p>Discard this payout? Its orders go back to Not yet commissioned. Nothing has been paid on it.</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                className="st-btn"
                style={{ background: '#8d3f2c', borderColor: '#8d3f2c' }}
                disabled={discard.isPending}
                onClick={() => discard.mutate(undefined, { onSuccess: () => setAsking(null) })}
              >
                {discard.isPending ? 'Discarding…' : 'Discard'}
              </button>
              <button type="button" className="st-btn-ghost" onClick={() => setAsking(null)}>
                Keep it
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button type="button" className="st-btn" disabled={!note.trim()} onClick={() => setAsking('confirm')}>
              Confirm
            </button>
            <button type="button" className="st-btn-ghost" onClick={() => setAsking('discard')}>
              Discard
            </button>
            {!note.trim() ? <span className="st-meta text-xs">Say how it&rsquo;s paid to confirm it.</span> : null}
            {save.isPending ? <span className="st-meta text-xs">Saving…</span> : null}
          </div>
        )}
        {error ? (
          <p className="st-alert mt-3" role="alert">
            {error.message}
          </p>
        ) : null}
      </div>
    </article>
  )
}

/** A confirmed payout in the history: when, to whom, how, who confirmed it — and its orders, on a tap. */
function ConfirmedPayout({ payout }: { readonly payout: CommissionPayoutView }) {
  const [open, setOpen] = useState(false)
  const flagged = flaggedItems(payout)
  return (
    <div className="py-2.5 text-sm" data-testid="confirmed-payout">
      <button type="button" className="flex w-full flex-wrap items-start gap-x-3 gap-y-1 text-left" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          {open ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
          <StaffMark mark={payout.staffMark} name={payout.staffName} />
          <span className="font-medium">{payoutHeadline(payout)}</span>
        </span>
        {flagged > 0 ? (
          <span className="st-badge st-badge--alert">{flagged === 1 ? '1 order changed' : `${flagged} orders changed`}</span>
        ) : null}
      </button>
      <div className="st-meta mt-0.5 pl-5 text-xs">
        {payout.note ? <span style={{ color: 'var(--st-ink)' }}>{payout.note}</span> : null}
        <span>
          {payout.note ? ' · ' : ''}
          {payout.items.length === 1 ? '1 order' : `${payout.items.length} orders`} · confirmed by {payout.confirmedByLabel ?? '—'}
          {payout.confirmedAt ? `, ${boutiqueDateTime(payout.confirmedAt)}` : ''}
        </span>
      </div>
      {open ? (
        <div className="mt-2">
          <PayoutItems items={payout.items} />
        </div>
      ) : null}
    </div>
  )
}

/**
 * A payout's orders, as they were calculated, with what has changed on each
 * since. Nothing is taken back automatically; the flag is for the owner.
 */
function PayoutItems({ items }: { readonly items: readonly CommissionPayoutItemView[] }) {
  return (
    <ul className="divide-y divide-[color:var(--st-rule)] border-t" style={{ borderColor: 'var(--st-rule)' }}>
      {items.map((item) => (
        <li key={item.id} className="flex items-start gap-3 px-4 py-2.5 text-sm" data-testid="payout-item">
          <span className="min-w-0 flex-1">
            <span className="block font-medium">{item.customerName}</span>
            <span className="st-meta block text-xs">
              <Link to={`/admin/orders/${item.orderId}`} className="underline decoration-[color:var(--st-rule-strong)] underline-offset-2">
                {item.orderNo}
              </Link>
            </span>
            {item.issues.length > 0 ? (
              <span className="mt-1 flex flex-wrap gap-1">
                {item.issues.map((issue) => (
                  <span key={issue} className="st-badge st-badge--alert">
                    {ISSUE_LABEL[issue]}
                  </span>
                ))}
              </span>
            ) : null}
          </span>
          <span className="shrink-0 text-right tabular-nums">
            <span className="block font-medium">{formatCents(item.amountMinor)}</span>
            <span className="st-meta block text-xs">{rateOfBase(item.rateBps, item.baseMinor)}</span>
          </span>
        </li>
      ))}
    </ul>
  )
}
